import StaticPage, { Section } from "../components/StaticPage";

export default function Accessibility() {
    return (
        <StaticPage
            title="Accessibility · ZenZest"
            description="Our accessibility commitment: WCAG 2.2 AA target, keyboard-first navigation, screen-reader-friendly cart, and a direct line to our team if anything blocks your visit."
            canonical="https://weeddispensarynearme.com/accessibility"
            eyebrow="Accessibility · last updated Feb 2026"
            h1="Built so every customer can shop."
            intro="We target WCAG 2.2 AA. Below is what that means in practice, what we do well, where we&rsquo;re still improving, and how to reach us if anything blocks your visit."
            toc={[
                { id: "commitment", label: "Our commitment" },
                { id: "standards", label: "Standards we target" },
                { id: "features", label: "What's in place" },
                { id: "gaps", label: "Known gaps" },
                { id: "in-store", label: "In-store access" },
                { id: "feedback", label: "Reach us" },
            ]}
        >
            <Section id="commitment" title="Our commitment">
                <p>
                    Cannabis retail is brand-new in New York, and access for
                    every adult is a baseline we take seriously. We design and
                    test our site so the same shopping experience is available
                    to keyboard-only users, screen-reader users, customers with
                    low vision, and customers with limited motor control.
                </p>
            </Section>

            <Section id="standards" title="Standards we target">
                <p>
                    Our target is WCAG 2.2 Level AA. We test against AA
                    success criteria on every major release. We also follow
                    the Section 508 federal accessibility guidelines for the
                    areas of our service that fall under them.
                </p>
            </Section>

            <Section id="features" title="What's in place today">
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        Semantic HTML — headings, landmarks, lists, and form
                        labels mapped per spec.
                    </li>
                    <li>
                        Keyboard-first navigation. Every interactive control
                        (menu, cart, filters, age gate, sign-in modal) is
                        reachable and operable via Tab / Shift+Tab / Enter /
                        Space / Escape.
                    </li>
                    <li>
                        Visible focus indicators on every interactive element —
                        no &quot;invisible focus&quot; pitfalls.
                    </li>
                    <li>
                        Color contrast targeting 4.5:1 for body text and 3:1
                        for large text per WCAG AA.
                    </li>
                    <li>
                        Form errors announced inline with associated{" "}
                        <code>aria-live</code> regions where appropriate.
                    </li>
                    <li>
                        Modals (age gate, cart drawer, loyalty sign-in) trap
                        focus correctly and restore it to the trigger on close.
                    </li>
                    <li>
                        Alt text on all product photography. Vendor specials
                        artwork includes the special name as a fallback
                        description.
                    </li>
                </ul>
            </Section>

            <Section id="gaps" title="Where we&rsquo;re still improving">
                <p>
                    No site is finished. The areas we&rsquo;re actively
                    improving:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        Map widget (Leaflet) — keyboard pan/zoom is functional
                        but not yet fully discoverable. A skip-link bypass is
                        in place.
                    </li>
                    <li>
                        Mobile filter chips on the menu — currently scroll
                        horizontally; we&rsquo;re moving to a dropdown for
                        narrower viewports.
                    </li>
                    <li>
                        Live announcements for cart updates (item added,
                        quantity changed) — partial coverage today; expanding
                        to full screen-reader narration in the next release.
                    </li>
                </ul>
            </Section>

            <Section id="in-store" title="In-store access">
                <p>
                    Both ZenZest flagships (Queens &amp; Staten Island) have
                    step-free entry, accessible checkout counters, and accessible
                    parking. Service animals welcome. If you need anything
                    specific for your visit, text the store ahead and we&rsquo;ll
                    plan around your needs.
                </p>
            </Section>

            <Section id="feedback" title="Reach us">
                <p>
                    If anything on this site blocks your shopping, please email{" "}
                    <a
                        href="mailto:accessibility@zenzest.com"
                        className="text-zen-ink underline"
                    >
                        accessibility@zenzest.com
                    </a>{" "}
                    with a quick description (page, action you were trying to
                    take, assistive tech you were using). We respond within two
                    business days and prioritize blockers in our next release.
                </p>
            </Section>
        </StaticPage>
    );
}
